import { supabase } from '@/integrations/supabase/client';
import type { Customer } from '@/hooks/useCustomers';
import { normalizePhoneToE164 } from '@sylva/shared-types';

/**
 * Normalizes a phone number to E.164 format
 * For German tenants, handles common German phone number formats
 * @param phone - The phone number to normalize
 * @param _isGermanTenant - Whether the tenant is German (deprecated parameter, no longer used)
 * @returns Normalized phone number in E.164 format (with + prefix)
 *
 * @deprecated Consider using normalizePhoneToE164 directly from @sylva/shared-types with explicit country code
 */
export const normalizePhoneNumber = (phone: string | null | undefined, _isGermanTenant: boolean): string => {
  if (!phone) return '';

  // Note: Always uses 'de' country code since most tenants are German
  // The isGermanTenant parameter is kept for backwards compatibility but no longer used
  const normalized = normalizePhoneToE164(phone, 'de');
  return normalized || '';
};

/**
 * Checks if a customer with the given first name and phone already exists
 * Matches the deduplication logic used in save-booking-lead function
 * @param tenantId - The tenant ID
 * @param _email - Customer email (not used for deduplication, kept for backwards compatibility)
 * @param phone - Customer phone number (E.164 format)
 * @param excludeCustomerId - Optional customer ID to exclude (for updates)
 * @param isGermanTenant - Whether the tenant is German
 * @param firstName - Customer first name (case-insensitive match)
 * @returns The existing customer if found, null otherwise
 */
export const checkDuplicateCustomer = async (
  tenantId: string,
  _email: string | null,
  phone: string | null,
  excludeCustomerId?: string,
  isGermanTenant: boolean = false,
  firstName?: string
) => {
  // Only search if we have a phone number to deduplicate on
  if (!phone || !phone.trim()) {
    console.log('[checkDuplicateCustomer] No phone provided, skipping deduplication');
    return null;
  }

  // First name is required for deduplication
  if (!firstName || !firstName.trim()) {
    console.log('[checkDuplicateCustomer] No first name provided, skipping deduplication');
    return null;
  }

  // Normalize inputs
  const normalizedPhone = normalizePhoneNumber(phone, isGermanTenant);
  const normalizedFirstName = firstName.trim();

  console.log(`[checkDuplicateCustomer] Searching for phone (E.164): ${normalizedPhone}, firstName: ${normalizedFirstName}`);

  // Query with exact phone match and case-insensitive first name match
  const query = supabase
    .from('customers')
    .select('*')
    .eq('tenant_id', tenantId)
    .eq('phone', normalizedPhone)
    .ilike('first_name', normalizedFirstName);

  if (excludeCustomerId) {
    query.neq('id', excludeCustomerId);
  }

  const { data: customer, error } = await query.maybeSingle();

  if (error) {
    console.error('Error checking for duplicate customer:', error);
    return null;
  }

  if (customer) {
    console.log(`[checkDuplicateCustomer] Found matching customer: ${customer.id}`);
  } else {
    console.log(`[checkDuplicateCustomer] No customer found with matching phone and first name`);
  }

  return customer;
};

/**
 * Creates a unique key for deduplication based on customer data
 * Matches the deduplication logic: phone (E.164) + case-insensitive first name
 * @param customer - Customer data with phone and first name
 * @param isGermanTenant - Whether the tenant is German
 * @returns Deduplication key in format "phone|firstName" (both normalized)
 */
const createCustomerKey = (customer: { email?: string | null, phone?: string | null, firstName: string, lastName: string | null }, isGermanTenant: boolean): string => {
  const normalizedPhone = normalizePhoneNumber(customer.phone, isGermanTenant);
  const normalizedFirstName = customer.firstName.trim().toLowerCase();
  // Only use phone + first name for deduplication (matches save-booking-lead logic)
  return `${normalizedPhone}|${normalizedFirstName}`;
};

/**
 * Deduplicates import data and batch-checks against existing customers
 * This is much more efficient than checking each customer individually
 */
export const deduplicateAndFilterImportData = async <T extends { email?: string | null, phone?: string | null, firstName: string, lastName: string | null, tenant_id: string }>(
  importData: T[],
  isGermanTenant: boolean = false
): Promise<{ newCustomers: T[], duplicates: T[], withinImportDuplicates: T[] }> => {
  if (importData.length === 0) {
    return { newCustomers: [], duplicates: [], withinImportDuplicates: [] };
  }

  const tenantId = importData[0].tenant_id;

  // Step 1: Deduplicate within the import data itself
  const uniqueCustomers = new Map<string, T>();
  const withinImportDuplicates: T[] = [];

  importData.forEach(customer => {
    const key = createCustomerKey(customer, isGermanTenant);
    if (uniqueCustomers.has(key)) {
      withinImportDuplicates.push(customer);
    } else {
      uniqueCustomers.set(key, customer);
    }
  });

  const deduplicatedImportData = Array.from(uniqueCustomers.values());

  // Step 2: Fetch all existing customers for the tenant ONCE
  const { data: existingCustomers, error } = await supabase
    .from('customers')
    .select('*')
    .eq('tenant_id', tenantId);

  if (error) {
    console.error('Error fetching existing customers:', error);
    // If we can't check, assume all are new (better than failing)
    return {
      newCustomers: deduplicatedImportData,
      duplicates: [],
      withinImportDuplicates
    };
  }

  // Step 3: Create lookup for existing customers
  const existingCustomerKeys = new Set<string>();
  (existingCustomers || []).forEach(customer => {
    const key = createCustomerKey({
      email: customer.email,
      phone: customer.phone,
      firstName: customer.first_name ?? '',
      lastName: customer.last_name ?? ''
    }, isGermanTenant);
    existingCustomerKeys.add(key);
  });

  // Step 4: Filter new vs duplicate customers
  const newCustomers: T[] = [];
  const duplicates: T[] = [];

  deduplicatedImportData.forEach(customer => {
    const key = createCustomerKey(customer, isGermanTenant);
    if (existingCustomerKeys.has(key)) {
      duplicates.push(customer);
    } else {
      newCustomers.push(customer);
    }
  });

  return { newCustomers, duplicates, withinImportDuplicates };
};

/**
 * Validates customer data before creation/update
 * @param data - Customer data to validate
 * @returns Object with isValid flag and error messages
 */
export const validateCustomerData = (data: {
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
}) => {
  const errors: string[] = [];

  if (!data.firstName?.trim()) {
    errors.push('First name is required');
  }

  if (!data.lastName?.trim()) {
    errors.push('Last name is required');
  }

  if (data.email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.email)) {
      errors.push('Invalid email format');
    }
  }

  if (data.phone) {
    const phoneRegex = /^[+]?[\d\s\-()]+$/;
    if (!phoneRegex.test(data.phone)) {
      errors.push('Invalid phone format');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Determines if a customer should be considered a lead (Interessent)
 *
 * @deprecated This function is deprecated. Use `customer.status === 'lead'` instead,
 * which is pre-computed in the customer_status database view for better performance.
 *
 * A customer is considered a lead if:
 * - They have no appointments AND
 * - They were not imported via bulk_import or whatsapp (existing salon customers)
 *
 * @param customer - The customer to check
 * @param customerIdsWithAppointments - Set of customer IDs that have appointments
 * @returns true if customer is a lead, false otherwise
 *
 * @example
 * // Old way (deprecated):
 * const isLead = isCustomerLead(customer, customerIdsWithAppointments);
 *
 * // New way (recommended):
 * const isLead = customer.status === 'lead';
 */
export const isCustomerLead = (
  customer: Customer,
  customerIdsWithAppointments: Set<string>
): boolean => {
  // If customer has appointments, they're not a lead
  if (customerIdsWithAppointments.has(customer.id)) {
    return false;
  }

  // If customer was bulk imported (existing salon customers) or from whatsapp, they're not a lead
  if (customer.acquisitionSource === 'bulk_import' || customer.acquisitionSource === 'whatsapp') {
    return false;
  }

  // Customer has no appointments and wasn't imported = lead
  return true;
};